import { Button } from '@/components/Button/Button';

import { ChatHtmlRenderer } from './ChatHtmlRenderer';
import type { ChatMessage } from './chatTypes';
import { MarkdownRenderer } from './MarkdownRenderer';
import styles from './MessageBubble.module.css';

interface MessageBubbleProps {
  // Exact URLs of this chapter's figures, so the renderer can allow them.
  chapterImageUrls?: ReadonlySet<string>;
  message: ChatMessage;
  onRetry?: (messageId: string) => void;
}

export function MessageBubble({
  chapterImageUrls,
  message,
  onRetry,
}: MessageBubbleProps) {
  const isUser = message.role === 'user';
  // Block type decides the renderer. Markdown blocks — everything stored before
  // HTML existed, plus any fallback — keep rendering as markdown.
  const blocks = message.blocks ?? [{ kind: 'markdown' as const, markdown: message.content }];

  return (
    <article
      className={styles.wrapper}
      data-role={message.role}
      data-status={message.status}
    >
      <div className={styles.bubble}>
        {isUser ? (
          <p className={styles.userText}>{message.content}</p>
        ) : (
          blocks.map((block, index) =>
            block.kind === 'html' ? (
              <ChatHtmlRenderer
                chapterImageUrls={chapterImageUrls}
                html={block.html}
                key={index}
              />
            ) : (
              <MarkdownRenderer content={block.markdown} key={index} />
            ),
          )
        )}
      </div>

      {message.status === 'error' && isUser ? (
        <div className={styles.errorRow}>
          <p className={styles.errorText}>{message.error ?? 'Failed to send message.'}</p>
          {onRetry ? (
            <Button onClick={() => onRetry(message.id)} size="small" variant="ghost">
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
